import React, {
  FC,
  useRef,
  useLayoutEffect,
  MutableRefObject,
  useCallback,
  useState,
  CSSProperties,
} from 'react';
import { resizeListen, resizeUnlisten } from 'dom-resize';
import { useSpring, animated, config, SpringConfig } from 'react-spring';
import { useIsMounted } from '@hox/frontend-utils/hooks/useIsMounted';
import { throttle } from '@hox/frontend-utils/ramda';

// Model
// -----------------------------------------------------------------------------

export interface IAnimatedResizingContentProps {
  style?: CSSProperties;
  axis?: 'x' | 'y' | 'xy';
}

// Hooks
// -----------------------------------------------------------------------------

const useTrackElementSize = (ref: MutableRefObject<HTMLElement | null>) => {
  const isMounted = useIsMounted();
  const [size, setSize] = useState({
    width: 0,
    height: 0,
  });

  const handleResize = useCallback(
    throttle(() => {
      if (!ref || !ref.current || !isMounted.current) {
        return;
      }
      setSize({
        width: ref.current.clientWidth,
        height: ref.current.clientHeight,
      });
    }, 120),
    [ref]
  );

  useLayoutEffect(() => {
    if (!ref || !ref.current) {
      return;
    }
    const el = ref.current;
    handleResize();
    resizeListen(el, handleResize);
    return () => {
      resizeUnlisten(el, handleResize);
    };
  }, [ref, handleResize]);

  return size;
};

// Component
// -----------------------------------------------------------------------------

export const AnimatedResizingContent: FC<IAnimatedResizingContentProps> = ({
  children,
  axis = 'y',
  ...restProps
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const contentSize = useTrackElementSize(contentRef);

  interface ISpringProps {
    config: SpringConfig;
    height?: number;
    width?: number;
  }
  const springProps: ISpringProps = {
    config: config.stiff,
  };
  if (axis.includes('y')) {
    springProps.height = contentSize.height;
  }
  if (axis.includes('x')) {
    springProps.width = contentSize.width;
  }

  const spring = useSpring(springProps);

  return (
    <animated.div {...restProps} style={{ ...spring, ...restProps.style }}>
      <div ref={contentRef}>{children}</div>
    </animated.div>
  );
};
